// @flow
import React, { Component } from 'react';
import { Keyboard, Platform, StyleSheet, View } from 'react-native';
import { Text } from 'trpc-react-native-ui';
import { Field } from 'redux-form';
import { DigitsInput, FullButton, GradientPage, Modal, SecondaryFullButton } from '../../../components/common';
import { PageTitle } from '../../../components/signUp';
import I18n from '../../../lib/i18n';
import pageStyle from '../styles';
import theme from '../../../theme-config';
import config from '../../../config';

import type { NavigationRoute, NavigationScreenProp } from '@react-navigation/compat';

export default class ValidateEmail extends Component<Props, State> {
  state: State = {
    isNoEmailReceivedModalVisible: false,
  };

  styles = getStyles();

  closeModal = () => this.setState({ isNoEmailReceivedModalVisible: false });
  showModal = () => {
    Keyboard.dismiss();
    this.setState({ isNoEmailReceivedModalVisible: true });
  };

  updateEmail = () => {
    this.closeModal();
    if (this.props.onUpdateEmail) {
      this.props.onUpdateEmail();
    } else {
      this.props.navigation.navigate('createAccount');
    }
  };

  resendEmail = () => {
    this.props.resendEmail();
    this.closeModal();
  };

  render() {
    const styles = this.styles;
    return (
      <GradientPage dismissKeyboard iosEdges={['top','bottom']} maxWidth={600}>
        <PageTitle title={this.props.titleOverride ?? I18n.t('SignUp.ValidateEmail.title')} style={pageStyle.title} />
        <View style={[pageStyle.form, styles.form]}>
          <View style={styles.errorContainer}>
            {!!this.props.submitFailed && !!this.props.error && <Text testID="otp-error-message" style={styles.error}>{this.props.error}</Text>}
          </View>
          <Text style={styles.text}>
            {this.props.textOverride ?? I18n.t('SignUp.ValidateEmail.text', {
              validationEmailNumberOfDigits: config.validationEmailNumberOfDigits,
            })}
          </Text>
          <Field
            autoFocus
            name="code"
            type="digits"
            component={DigitsInput}
            style={styles.input}
            returnKeyType="go"
            maxLength={config.validationEmailNumberOfDigits}
            onSubmitEditing={this.props.handleSubmit}
            testID={'validateEmail-code-input'}
          />
          <FullButton
            onPress={this.props.handleSubmit}
            isLoading={this.props.isLoading}
            text={I18n.t('validate')}
            style={styles.button}
            disabled={!this.props.valid}
            testID={'validateEmail-submit-button'}
          />
          {this.props.attemptsRemaining != null && (
            <Text style={styles.attemptsRemaining}>
              {I18n.t('Login.OtpVerification.attemptsRemaining', {count: this.props.attemptsRemaining})}
            </Text>
          )}
          <View style={styles.buttonContainer}>
            <SecondaryFullButton
              testID="otp-noCode-button"
              style={styles.secondaryFullButton}
              onPress={this.showModal}
              text={this.props.noEmailLinkOverride ?? I18n.t('SignUp.ValidateEmail.noEmailLink')}
              isLoading={this.props.isResending}
            />
          </View>

          <Modal visible={this.state.isNoEmailReceivedModalVisible} requestClose={this.closeModal}>
            <View style={styles.modalContainer}>
              <View style={styles.modalTitleContainer}>
                <Text style={styles.modalTitle}>
                  {this.props.modalTitleOverride ?? I18n.t('SignUp.ValidateEmail.modal.title')}
                </Text>
              </View>
              <View style={styles.modalTextContainer}>
                <Text style={styles.modalText}>
                  {this.props.modalTextOverride ?? I18n.t('SignUp.ValidateEmail.modal.text')}
                </Text>
                <Text style={styles.modalEmail}>{this.props.email}</Text>
              </View>
              <View style={styles.modalButtonsContainer}>
                <FullButton
                  testID="otp-resend-button"
                  style={styles.button}
                  text={this.props.modalResendOverride ?? I18n.t('SignUp.ValidateEmail.modal.resend')}
                  onPress={this.resendEmail}
                />
                <FullButton
                  testID="otp-updateEmail-button"
                  style={styles.modalButton}
                  text={this.props.modalUpdateTextOverride ?? I18n.t('SignUp.ValidateEmail.modal.update')}
                  onPress={this.updateEmail}
                  neutralColors
                />
              </View>
            </View>
          </Modal>
        </View>
      </GradientPage>
    );
  }
}

type Props = {
  email: string,
  error?: string,
  handleSubmit: Function,
  isLoading: boolean,
  isResending?: boolean,
  // Optional: only provided in OTP login context, not in SignUp flow (doc fonctionnelle §6)
  attemptsRemaining?: number | null,
  modalResendOverride?: string,
  modalTextOverride?: string,
  modalTitleOverride?: string,
  modalUpdateTextOverride?: string,
  navigation: NavigationScreenProp<NavigationRoute>,
  noEmailLinkOverride?: string,
  onUpdateEmail?: Function,
  resendEmail: Function,
  submitFailed: boolean,
  titleOverride?: string,
  textOverride?: string,
  valid: boolean,
};

type State = {
  isNoEmailReceivedModalVisible: boolean,
};

function getStyles() {
  return StyleSheet.create({
    form: {
      marginTop: 10,
    },
    text: {
      fontSize: 18,
      backgroundColor: 'transparent',
      textAlign: 'center',
      color: theme.signUpDarkTextColor,
      marginBottom: 20,
    },
    button: {
      marginTop: 20,
    },
    input: {
      marginVertical: 8,
      color: theme.signUpTextColor,
    },
    secondaryFullButton: {
      fontSize: 15,
      color: theme.signUpTextColor,
    },
    buttonContainer: {
      marginTop: 10,
    },
    modalContainer: {
      marginHorizontal: 8,
    },
    modalTitleContainer: {
      marginVertical: 25,
    },
    modalTitle: {
      textAlign: 'center',
      color: theme.primaryColor,
      fontSize: 25,
    },
    modalTextContainer: {
      alignItems: 'center',
    },
    modalText: {
      textAlign: 'center',
      fontSize: 18,
    },
    modalEmail: {
      fontSize: 18,
      fontWeight: 'bold',
    },
    modalButtonsContainer: {},
    modalButton: {
      marginVertical: 13,
    },
    errorContainer: {
      height: 45,
      marginBottom: 10,
    },
    error: {
      color: theme.primaryColor,
      backgroundColor: 'transparent',
      fontSize: 16,
      textAlign: 'center',
    },
    attemptsRemaining: {
      fontSize: 13,
      textAlign: 'center',
      color: theme.primaryColor,
      marginTop: 8,
    },
  });
}
